import * as Yup from "yup";
import api from "../services/api";

/**
 * Constantes de tipos de eventos disponibles
 */
export const TIPOS_EVENTO = [
  "XV Años",
  "Boda",
  "Bautizo",
  "XVIII Años",
  "Cumpleaños",
  "Graduación",
  "Comunión",
  "Corporativo",
  "Otro",
];

/**

/**
 * Calcula la fecha mínima permitida para un evento
 * - Administradores: desde 01/01/2026 (para cargar datos históricos)
 * - Usuarios normales: día siguiente a hoy en zona horaria de México City
 * Considera la zona horaria de México City (UTC-6)
 */
const getMinimumEventDate = (isAdmin = false) => {
  if (isAdmin) {
    // Para administradores: permitir desde el 1 de enero de 2026
    const minimumDate = new Date("2026-01-01T00:00:00");
    return minimumDate;
  }

  // Para usuarios normales: día siguiente a hoy
  const now = new Date();
  const offset = -6; // UTC-6 para México City
  const localOffset = now.getTimezoneOffset() / 60; // Offset local en horas
  const difference = offset - localOffset;
  const mexicoNow = new Date(now.getTime() + difference * 60 * 60 * 1000);

  // Calcular el día siguiente a las 00:00:00
  const tomorrow = new Date(mexicoNow);
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);

  return tomorrow;
};

// Validación asíncrona para cliente existente
// NOTA: Deshabilitada - los clientes vienen de listas ya validadas
const validateClienteExists = async (clienteId) => {
  if (!clienteId) return true;
  // La validación real se hace en el servidor al guardar
  // No hacer llamadas API innecesarias durante la edición
  return true;
};

// Validación para colaboradores
// NOTA: Deshabilitada - los colaboradores vienen de listas ya validadas
const validateColaboradoresExist = async (ids) => {
  if (!Array.isArray(ids) || ids.length === 0) return true;
  // La validación real se hace en el servidor al guardar
  return true;
};

// Validación de disponibilidad de colaboradores en fecha
const validateColaboradoresAvailability = async (data) => {
  const { colaboradores_ids, fecha_inicio, fecha_fin } = data;
  if (!Array.isArray(colaboradores_ids) || !fecha_inicio || !fecha_fin) {
    return true;
  }
  try {
    const response = await api.post("/colaboradores/check-availability", {
      colaboradores_ids,
      fecha_inicio,
      fecha_fin,
    });
    return response.data.available;
  } catch {
    throw new Error("Error verificando disponibilidad de colaboradores");
  }
};

/**
 * Schema principal de validación para Eventos
 * Se genera dinámicamente según el rol del usuario
 */
export const getEventoValidationSchema = (isAdmin = false) =>
  Yup.object().shape({
    // Tipo de Evento
    tipo_evento: Yup.string()
      .required("El tipo de evento es requerido")
      .oneOf(TIPOS_EVENTO, "Debes seleccionar un tipo de evento válido"),

    descripcion: Yup.string()
      .required("La descripción es requerida")
      .min(10, "La descripción debe tener al menos 10 caracteres")
      .max(1000, "La descripción no puede exceder 1000 caracteres"),

    fecha_inicio: Yup.date()
      .required("La fecha de inicio es requerida")
      .min(
        getMinimumEventDate(isAdmin),
        isAdmin
          ? "La fecha de inicio debe ser a partir del 1 de enero de 2026"
          : "La fecha de inicio debe ser al menos el día siguiente a hoy (en zona horaria de México City)",
      ),

    fecha_fin: Yup.date()
      .required("La fecha de fin es requerida")
      .min(
        Yup.ref("fecha_inicio"),
        "La fecha de fin debe ser posterior a la fecha de inicio",
      ),

    hora_inicio: Yup.string().required("La hora de inicio es requerida"),

    hora_fin: Yup.string()
      .required("La hora de fin es requerida")
      .test(
        "hora_valida",
        "La hora de fin debe ser posterior a la hora de inicio",
        function (value) {
          const { hora_inicio } = this.parent;
          if (!hora_inicio || !value) return true;
          return value > hora_inicio;
        },
      ),

    ubicacion: Yup.string()
      .required("La ubicación es requerida")
      .min(5, "La ubicación debe tener al menos 5 caracteres"),

    // Relación con Cliente
    cliente_id: Yup.number()
      .required("Debes seleccionar un cliente")
      .positive("ID de cliente inválido")
      .test("cliente-exists", "Cliente no encontrado", validateClienteExists),

    // Relación con Colaboradores
    colaboradores_ids: Yup.array()
      .of(Yup.number().positive())
      .required("Debes seleccionar al menos un colaborador")
      .min(1, "Se requiere al menos un colaborador")
      .max(20, "No se pueden seleccionar más de 20 colaboradores")
      .test(
        "colaboradores-exist",
        "Algunos colaboradores no existen",
        validateColaboradoresExist,
      ),

    // Relación con Servicios
    servicios: Yup.array()
      .of(
        Yup.object().shape({
          servicio_id: Yup.number()
            .nullable()
            .transform((value, originalValue) =>
              originalValue === null || originalValue === "" ? null : value,
            )
            .when("$validateServiceIds", {
              is: true,
              then: (schema) =>
                schema
                  .required("Debes seleccionar un servicio")
                  .positive("ID de servicio inválido"),
              otherwise: (schema) => schema.nullable(),
            }),

          cantidad: Yup.number()
            .required("La cantidad es requerida")
            .min(1, "La cantidad debe ser mayor a 0")
            .max(9999, "La cantidad no puede exceder 9999"),

          precio_unitario: Yup.number()
            .required("El precio es requerido")
            .min(0, "El precio no puede ser negativo")
            .max(999999, "El precio es demasiado alto"),

          total: Yup.number()
            .required()
            .min(0, "El total no puede ser negativo")
            .test(
              "total-correcto",
              "El total no coincide con cantidad × precio",
              function (value) {
                const { cantidad, precio_unitario } = this.parent;
                if (!cantidad || precio_unitario === undefined) return true;
                return (
                  Math.abs((value || 0) - cantidad * precio_unitario) < 0.01
                );
              },
            ),
        }),
      )
      .test(
        "servicios-validos",
        "Todos los servicios deben tener un servicio seleccionado",
        function (value) {
          if (!value || value.length === 0) return true;
          return value.every(
            (item) => item.servicio_id !== null && item.servicio_id > 0,
          );
        },
      )
      .max(50, "No se pueden agregar más de 50 servicios"),

    // Relación con Paquetes
    paquetes: Yup.array()
      .of(
        Yup.object().shape({
          paquete_id: Yup.number()
            .nullable()
            .transform((value, originalValue) =>
              originalValue === null || originalValue === "" ? null : value,
            )
            .when("$validatePaqueteIds", {
              is: true,
              then: (schema) =>
                schema
                  .required("Debes seleccionar un paquete")
                  .positive("ID de paquete inválido"),
              otherwise: (schema) => schema.nullable(),
            }),

          cantidad: Yup.number()
            .required("La cantidad es requerida")
            .min(1, "La cantidad debe ser mayor a 0")
            .max(9999, "La cantidad no puede exceder 9999"),

          precio_unitario: Yup.number()
            .required("El precio es requerido")
            .min(0, "El precio no puede ser negativo")
            .max(999999, "El precio es demasiado alto"),

          total: Yup.number()
            .required()
            .min(0, "El total no puede ser negativo")
            .test(
              "total-correcto",
              "El total no coincide con cantidad × precio",
              function (value) {
                const { cantidad, precio_unitario } = this.parent;
                if (!cantidad || precio_unitario === undefined) return true;
                return (
                  Math.abs((value || 0) - cantidad * precio_unitario) < 0.01
                );
              },
            ),
        }),
      )
      .test(
        "paquetes-validos",
        "Todos los paquetes deben tener un paquete seleccionado",
        function (value) {
          if (!value || value.length === 0) return true;
          return value.every(
            (item) => item.paquete_id !== null && item.paquete_id > 0,
          );
        },
      )
      .max(50, "No se pueden agregar más de 50 paquetes"),

    // Relación con Insumos
    insumos: Yup.array()
      .of(
        Yup.object().shape({
          insumo_id: Yup.number()
            .nullable()
            .transform((value, originalValue) =>
              originalValue === null || originalValue === "" ? null : value,
            ),

          cantidad: Yup.number()
            .required("La cantidad es requerida")
            .min(1, "La cantidad debe ser mayor a 0")
            .max(9999, "La cantidad no puede exceder 9999"),

          costo_unitario: Yup.number()
            .required("El costo es requerido")
            .min(0, "El costo no puede ser negativo")
            .max(999999, "El costo es demasiado alto"),

          subtotal: Yup.number()
            .required()
            .min(0, "El subtotal no puede ser negativo")
            .test(
              "subtotal-correcto",
              "El subtotal no coincide con cantidad × costo",
              function (value) {
                const { cantidad, costo_unitario } = this.parent;
                if (!cantidad || costo_unitario === undefined) return true;
                return (
                  Math.abs((value || 0) - cantidad * costo_unitario) < 0.01
                );
              },
            ),
        }),
      )
      .test(
        "insumos-validos",
        "Todos los insumos deben tener un insumo seleccionado",
        function (value) {
          if (!value || value.length === 0) return true;
          return value.every(
            (item) => item.insumo_id !== null && item.insumo_id > 0,
          );
        },
      )
      .max(100, "No se pueden agregar más de 100 insumos"),

    // Información financiera
    monto_paquete: Yup.number()
      .required("El monto del paquete es requerido")
      .min(0, "El monto no puede ser negativo")
      .max(999999, "El monto es demasiado alto"),

    adelanto: Yup.number()
      .required("El adelanto es requerido")
      .min(500, "El adelanto mínimo es de $500")
      .max(
        Yup.ref("monto_paquete"),
        "El adelanto no puede exceder el monto del paquete",
      ),

    estado: Yup.string()
      .required("Debes seleccionar un estado")
      .oneOf(
        ["Por Confirmar", "Presupuesto", "Confirmado", "En curso", "Completado", "Cancelado"],
        "Estado no válido",
      ),

    // Notas y observaciones
    notas: Yup.string().max(
      1000,
      "Las notas no pueden exceder 1000 caracteres",
    ),
  });

/**
 * Schema por defecto para usuarios normales (compatibilidad)
 */
export const eventoValidationSchema = getEventoValidationSchema(false);

/**
 * Schema simplificado para edición rápida
 */
export const eventoSimplifiedSchema = Yup.object().shape({
  nombre: Yup.string()
    .required("El nombre del evento es requerido")
    .max(255, "El nombre no puede exceder 255 caracteres"),

  fecha_inicio: Yup.date()
    .required("La fecha de inicio es requerida")
    .min(new Date(), "La fecha de inicio no puede ser en el pasado"),

  fecha_fin: Yup.date()
    .required("La fecha de fin es requerida")
    .min(Yup.ref("fecha_inicio"), "La fecha de fin debe ser posterior"),

  cliente_id: Yup.number()
    .required("Debes seleccionar un cliente")
    .positive("ID de cliente inválido"),

  estado: Yup.string()
    .required("Debes seleccionar un estado")
    .oneOf([
      "Por Confirmar",
      "Presupuesto",
      "Confirmado",
      "En curso",
      "Completado",
      "Cancelado",
    ]),
});

/**
 * Función para validar el formulario completo
 * Incluye validación transaccional
 */
export const validateEventoFull = async (values) => {
  try {
    // Validación básica
    await eventoValidationSchema.validate(values, { abortEarly: false });

    // Validación de disponibilidad de colaboradores
    await validateColaboradoresAvailability(values);

    return { valid: true, errors: {} };
  } catch (error) {
    if (error.inner) {
      const errors = {};
      error.inner.forEach((err) => {
        errors[err.path] = err.message;
      });
      return { valid: false, errors };
    }
    return { valid: false, errors: { general: error.message } };
  }
};

/**
 * Función para obtener valores iniciales del formulario
 */
export const getInitialEventoValues = () => ({
  titulo: "",
  tipo_evento: "",
  descripcion: "",
  fecha_inicio: new Date(),
  fecha_fin: new Date(),
  hora_inicio: "09:00",
  hora_fin: "17:00",
  ubicacion: "",
  direccion: "",
  cliente_id: null,
  colaboradores_ids: [],
  servicios: [],
  paquetes: [],
  insumos: [],
  monto_paquete: 0,
  adelanto: 0,
  estado: "Por Confirmar",
  notas: "",
  paquete_id: null,
  tipo_servicio_id: null,
  canal: "",
});

/**
 * Función para transformar evento desde API a formato de formulario
 */
export const transformEventoToForm = (evento) => {
  return {
    titulo: evento.titulo || evento.nombre || "",
    tipo_evento: evento.nombre || evento.titulo || "",
    descripcion: evento.descripcion || "",
    fecha_inicio: evento.fecha_inicio
      ? new Date(evento.fecha_inicio)
      : new Date(),
    fecha_fin: evento.fecha_fin ? new Date(evento.fecha_fin) : new Date(),
    hora_inicio: evento.hora_inicio || "09:00",
    hora_fin: evento.hora_fin || "17:00",
    ubicacion: evento.ubicacion || "",
    direccion: evento.direccion || evento.ubicacion || "",
    cliente_id: evento.cliente_id || null,
    colaboradores_ids: Array.isArray(evento.colaboradores_ids)
      ? evento.colaboradores_ids
      : (evento.colaboradores || []).map((c) => c.id),
    servicios: (evento.servicios || []).map((s) => ({
      servicio_id: s.id || s.servicio_id,
      cantidad: s.cantidad || 1,
      precio_unitario: s.precio || s.precio_unitario || 0,
      total: (s.cantidad || 1) * (s.precio || s.precio_unitario || 0),
    })),
    paquetes: (evento.paquetes || []).map((p) => ({
      paquete_id: p.id || p.paquete_id,
      cantidad: p.cantidad || 1,
      precio_unitario: p.precio || p.precio_unitario || 0,
      total: (p.cantidad || 1) * (p.precio || p.precio_unitario || 0),
    })),
    insumos: (evento.insumos || []).map((i) => ({
      insumo_id: i.id || i.insumo_id,
      cantidad: i.cantidad || 1,
      costo_unitario: i.costo || i.costo_unitario || 0,
      subtotal: (i.cantidad || 1) * (i.costo || i.costo_unitario || 0),
    })),
    monto_paquete: evento.monto_paquete || evento.costo_servicio || 0,
    adelanto: evento.adelanto || evento.anticipo || 0,
    estado: evento.estado || "Por Confirmar",
    notas: evento.notas || "",
    paquete_id: evento.paquete_id || null,
    tipo_servicio_id: evento.tipo_servicio_id || null,
    canal: evento.canal || "",
  };
};

/**
 * Función para transformar datos del formulario a formato API
 */
export const transformFormToEvento = (values, eventoId = null) => {
  return {
    id: eventoId,
    // titulo guarda el tipo de evento + descripción breve si la hay
    titulo: values.titulo || values.tipo_evento || "",
    nombre: values.tipo_evento, // Tipo de evento como respaldo
    descripcion: values.descripcion || "",
    fecha_inicio:
      values.fecha_inicio instanceof Date
        ? values.fecha_inicio.toISOString().split("T")[0]
        : values.fecha_inicio,
    fecha_fin:
      values.fecha_fin instanceof Date
        ? values.fecha_fin.toISOString().split("T")[0]
        : values.fecha_fin,
    hora_inicio: values.hora_inicio,
    hora_fin: values.hora_fin,
    ubicacion: values.ubicacion,
    direccion: values.direccion || values.ubicacion || "",
    cliente_id: values.cliente_id,
    monto_paquete: parseFloat(values.monto_paquete) || 0,
    adelanto: parseFloat(values.adelanto) || 0,
    estado: values.estado,
    notas: values.notas || "",
    colaboradores_ids: [...new Set((values.colaboradores_ids || []).map(Number).filter(Boolean))],
    paquete_id: values.paquete_id || null,
    tipo_servicio_id: values.tipo_servicio_id || null,
    canal: values.canal || "",
    servicios: (values.servicios || []).map((s) => ({
      servicio_id: s.servicio_id,
      cantidad: parseInt(s.cantidad) || 1,
      precio: parseFloat(s.precio_unitario) || 0,
    })),
    paquetes: (values.paquetes || []).map((p) => ({
      paquete_id: p.paquete_id,
      cantidad: parseInt(p.cantidad) || 1,
      precio: parseFloat(p.precio_unitario) || 0,
    })),
    insumos: (values.insumos || []).map((i) => ({
      insumo_id: i.insumo_id,
      cantidad: parseInt(i.cantidad) || 1,
      costo: parseFloat(i.costo_unitario) || 0,
    })),
  };
};
